import { useState } from 'react';
import jwt from 'jsonwebtoken';
import axios from 'axios';
import { ToastService } from '../services/ToastService';
import { applyToRecommendedJob, submitAsNotInterested } from '../legacy_components/services/commonAPI';
import { pushLog2DV2LogServer } from '../services/logging';
import { ApiConstant } from '../constants';
import { useDispatch, useSelector } from 'react-redux';
import { getJobInterestInfo, saveJobInterestInfo } from '../components/JobInterest/job-interest/job-interest.actions';
import { convertValueToHourlyAmount, convertValueToMonthlyAmount, getAmountInUSDWithCurrencyRate } from '../services/util/currency';
import { APPLICATION_STATUS } from '../legacy_components/UI_V2_Components/jobsPortal/constants';


export const logJobDashboardActivity = (category, activity, payload) => {
    const token = localStorage.getItem('token');
    const user = jwt.decode(token);
    pushLog2DV2LogServer.info(
        category,
        activity,
        { user_id: user.uid },
        { user_id: user.uid, timestamp: new Date(), ...payload },
    );
}


export const useJobActions = (job = null, isRecommended = false, options = {}) => {
    const { jobs = [], updateJobsV2 } = options;
    const [loading, setLoading] = useState(false);
    const [submittingNotInterested, setSubmittingNotInterested] = useState(false);
    const agencyInfo = useSelector(state => state.profile.agency_info);
    const { 
        isFetching: isJobInterestFetching,
        jobInterest: {
            devCurrencyRate
        }
    } = useSelector(state => state.jobInterest);
    const dispatch = useDispatch();

    const displayAlreadySubmittedToast = async (jobId) => {
        try {
            const response = await axios.get(`${ApiConstant.BASE_URL}job/public`, {
                params: { jobId }
            });

            if (!response.data?.success || !response.data?.result?.job_title) {
                return;
            }

            ToastService.info(`You already applied for ${response.data.result.job_title}. You can check your application status in the jobs list.`)
        } catch (error) {
            console.error('Error fetching job details:', error);
        }
    };

    const handleJobApplication = async (callbacks = {}, jobCode = null) => {
        const {
            onShowJobRequestModal,
            onOpenVetsmithModal,
            onApplicationComplete
        } = callbacks;

        setLoading(true);
        try {
            const res = await applyToRecommendedJob(
                jobCode ? null : job?.id,
                jobCode ? "RECOMMENDED_JOB" : (isRecommended ? "RECOMMENDED_JOB_V2" : null),
                jobCode
            );

            const {
                data: {
                    result: {
                        url = null,
                        idHash = null,
                        submittedAt,
                        hasApplied = false,
                        vetsmithFlowLink = null,
                        vetsmithFlowCompleted = false,
                        icfRequired = true,
                        jobId,
                        blockSubmissionReason
                    } = {}
                } = {}
            } = res;

            if (updateJobsV2 && jobs.length) {
                updateJobsV2(jobs.map(job => {
                    if (job.jobId === jobId) {
                        return {
                            ...job,
                            canResumeApplication: true,
                            status: "VIEWED",
                            applicationStatus: APPLICATION_STATUS.TAKE_VETTING
                        }
                    }
                    return job;
                }));
            }

            if (idHash && !submittedAt) {
                if (icfRequired) {
                    onShowJobRequestModal?.(idHash, jobId, blockSubmissionReason);
                } else {
                    const targetJob = jobCode ? jobs.find(j => j.jobCode === jobCode) : job;
                    dispatch(getJobInterestInfo(idHash))
                    onOpenVetsmithModal?.(targetJob);
                }
            } else {
                if (hasApplied && vetsmithFlowLink && !vetsmithFlowCompleted) {
                    window.location.href = `/dashboard/jobs/${jobId}/specialized-vetting`;
                } else {
                    jobCode ? await displayAlreadySubmittedToast(jobId) : window.location.href = url;
                }
            }
            onApplicationComplete?.();
        } catch (error) {
            console.error('Job application failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNotInterested = async (data, onComplete) => {
        const { notInterestedReasonSystemName, notInterestedFeedback } = data;
        setSubmittingNotInterested(true);

        try {
            await submitAsNotInterested(
                job.id,
                isRecommended ? "RECOMMENDED_JOB_V2" : null,
                notInterestedReasonSystemName,
                notInterestedFeedback
            );

            logJobDashboardActivity('JOB_APPLICATIONS', 'CLICKED_ON_NOT_INTERESTED_IN_JOB', {
                jobId: job.id
            });

            onComplete?.();
        } catch (error) {
            console.error('Not interested submission failed:', error);
        } finally {
            setSubmittingNotInterested(false);
        }
    };

    const autoSubmitUserJobInterest = (job) => { 
        const userAgencyData = agencyInfo.find(agencyData => agencyData.job_id === job.id); 
        const {
            expected_salary,
            salary_currency,
            salary_frequency
        } = userAgencyData;
        let amountInUsd = expected_salary;
        if ('usd' !== salary_currency.toLowerCase()) {
            amountInUsd = getAmountInUSDWithCurrencyRate(expected_salary, devCurrencyRate)
        }

        let monthlyAmount = 0, hourlyAmount = 0;
        if (salary_frequency === 'hourly') {
            monthlyAmount = convertValueToMonthlyAmount('hourly', amountInUsd);
            hourlyAmount = amountInUsd;
        } else {
            monthlyAmount = convertValueToMonthlyAmount('yearly', amountInUsd)
            hourlyAmount = convertValueToHourlyAmount('yearly', amountInUsd);
        }

        dispatch(saveJobInterestInfo(
            {
                idHash: job.jobRequestCode,
                isInterested: true,
                availabilityConfirmed: true,
                canOverlapWithTimezone: true,
                newDesiredSalaryHourly: hourlyAmount,
                newDesiredSalaryMonthly: monthlyAmount,
                dataIntegrityConfirmed: true,
                salaryConfirmed: true,
                sumissionSource: 'JRF_JOB_DASHBOARD_AUTO_SUBMIT'
            },
            `/dashboard/jobs/${job.id}/specialized-vetting`
        ))
    };

    return {
        loading,
        submittingNotInterested,
        handleJobApplication,
        handleNotInterested,
        autoSubmitUserJobInterest,
        isJobInterestFetching
    };
};